import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldAlert, ArrowLeft, Home, Lock, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get the route they tried to access and missing permissions
  const from = (location.state as any)?.from?.pathname || "/";
  const missingPermissions = (location.state as any)?.missingPermissions || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 flex items-center justify-center p-4">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');
        
        * {
          font-family: 'Sora', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .float-animation {
          animation: float 3s ease-in-out infinite;
        }

        .fade-in {
          animation: fadeIn 0.6s ease-out forwards;
        }
      `}</style>

      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
          </div>

          <div className="relative float-animation">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-4 shadow-lg">
              <ShieldAlert className="w-12 h-12 text-red-500" />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-white mb-2 relative">
            Access Denied
          </h1>
          <p className="text-red-100 text-lg relative">
            You don't have permission to access this page
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* User Info */}
          {user && (
            <div className="bg-gray-50 rounded-2xl p-6 mb-6 border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {user.firstName?.charAt(0).toUpperCase() || "U"}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {user.firstName + " " + user.lastName}
                  </p>
                  <p className="text-sm text-gray-600">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Lock className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Current Role:</span>
                <span className="font-semibold text-gray-900 capitalize">
                  {user.role || "No Role"}
                </span>
              </div>
            </div>
          )}

          {/* Attempted Route */}
          {from && from !== "/unauthorized" && (
            <div className="bg-[#0d223f] border border-[#0d223f] rounded-2xl p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Attempted to access:</strong>{" "}
                <code className="bg-[#0d223f] px-2 py-1 rounded">{from}</code>
              </p>
            </div>
          )}

          {/* Missing Permissions */}
          {missingPermissions.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Required Permissions
              </h3>
              <div className="space-y-2">
                {missingPermissions.map((permission: string, index: number) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-sm text-red-700 bg-white rounded-lg px-3 py-2"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                    <code className="text-xs">{permission}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Information */}
          <div className="bg-[#0d223f] border border-[#0d223f] rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5" />
              What can I do?
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0d223f] mt-2"></div>
                <span>
                  Contact your administrator to request access to this feature
                </span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0d223f] mt-2"></div>
                <span>Return to the previous page or dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0d223f] mt-2"></div>
                <span>
                  Verify that you're logged in with the correct account
                </span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Go Back</span>
            </button>

            <button
              onClick={() => navigate("/")}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg"
            >
              <Home className="w-5 h-5" />
              <span>Go to Home</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-4 text-center border-t border-gray-600">
          <p className="text-sm text-gray-600">
            Need help? Contact your system administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage;
